import { useEffect, useRef } from 'react';
import securityConfig from '../config/security';

export const useDevToolsProtection = () => {
  const intervalRef = useRef(null);
  const isProtectionActive = useRef(true);

  // Mobile detection to prevent false positives
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768 ||
           ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0);
  };

  const redirectToBlankPage = () => {
    // Clear the entire page content
    document.documentElement.innerHTML = `
      <html>
        <head>
          <title>Access Denied</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              background: #000;
              color: #fff;
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              text-align: center;
            }
            .container {
              max-width: 400px;
              padding: 20px;
            }
            .warning {
              font-size: 24px;
              margin-bottom: 20px;
              color: #ff4444;
            }
            .message {
              font-size: 16px;
              line-height: 1.5;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="warning">⚠️ ACCESS DENIED</div>
            <div class="message">
              Developer tools are not allowed on this website.<br>
              Please close the developer tools and refresh the page.
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Prevent back navigation
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = () => {
      window.history.pushState(null, null, window.location.href);
    };
  };

  // Immediate detection for pre-opened DevTools
  const immediateDetection = () => {
    if (isMobile()) return false;

    // Check window size immediately
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;
    
    if ((widthDiff > 200 || heightDiff > 200) && !document.fullscreenElement) {
      console.log('DevTools detected at page load (window size)');
      redirectToBlankPage();
      return true;
    }

    // Console detection trick for immediate check
    let detected = false;
    const element = new Image();
    Object.defineProperty(element, 'id', {
      get: function() {
        detected = true;
      }
    });
    console.log('%c', element);
    console.clear();
    
    if (detected) {
      console.log('DevTools detected at page load (console)');
      redirectToBlankPage();
      return true;
    }

    // Method 3: Debugger timing test
    const start = performance.now();
    debugger;
    const debuggerTime = performance.now() - start;
    
    if (debuggerTime > 100) {
      console.log('DevTools detected at page load (debugger timing)');
      redirectToBlankPage();
      return true;
    }

    // Method 4: Check viewport ratio (DevTools changes this)
    const viewportRatio = window.innerHeight / window.innerWidth;
    const screenRatio = window.screen.height / window.screen.width;
    
    // If viewport ratio is significantly different from screen ratio, DevTools might be open
    if (Math.abs(viewportRatio - screenRatio) > 0.3 && !document.fullscreenElement) {
      console.log('DevTools detected at page load (viewport ratio)');
      redirectToBlankPage();
      return true;
    }

    return false;
  };

  const detectDevTools = () => {
    // Skip aggressive detection on mobile
    if (isMobile()) {
      console.log('Mobile device detected - skipping DevTools detection');
      return;
    }

    // Multi-method detection for better accuracy (DESKTOP ONLY)
    let devtools = { open: false, orientation: null };
    
    // Method 1: Console timing detection - higher threshold
    const checkDebuggerTiming = () => {
      if (!isProtectionActive.current) return;
      
      let threshold = 300; // Higher threshold for mobile compatibility
      let start = performance.now();
      debugger;
      let end = performance.now();
      
      if (end - start > threshold) {
        devtools.open = true;
        redirectToBlankPage();
      }
    };

    // Method 2: Window size detection (more accurate) - DESKTOP ONLY
    const checkWindowSize = () => {
      if (!isProtectionActive.current || isMobile()) return;
      
      const widthThreshold = window.outerWidth - window.innerWidth > 300; // Much higher threshold
      const heightThreshold = window.outerHeight - window.innerHeight > 300;
      
      // Only trigger if both dimensions suggest DevTools AND we're not in fullscreen
      if ((widthThreshold || heightThreshold) && 
          !document.fullscreenElement && 
          window.screen.availHeight === window.screen.height) {
        devtools.open = true;
        redirectToBlankPage();
      }
    };

    // Method 3: Console detection
    const checkConsole = () => {
      if (!isProtectionActive.current) return;
      
      // Check if console is being used actively
      const element = new Image();
      Object.defineProperty(element, 'id', {
        get: function() {
          devtools.open = true;
          redirectToBlankPage();
        }
      });
      console.log(element);
    };

    // Method 4: DevTools open detection via console.log override
    const detectConsoleOpen = () => {
      if (!isProtectionActive.current) return;
      
      const devtools = {open: false, orientation: null};
      
      setInterval(() => {
        if (window.outerHeight - window.innerHeight > 160 || 
            window.outerWidth - window.innerWidth > 160) {
          if (!devtools.open) {
            devtools.open = true;
            redirectToBlankPage();
          }
        }
      }, 500);
    };

    // Start checking after initial delay
    setTimeout(() => {
      if (isProtectionActive.current) {
        // Check multiple methods with different intervals
        setInterval(checkDebuggerTiming, 1000);
        setInterval(checkWindowSize, 2000);
        setInterval(checkConsole, 3000);
        detectConsoleOpen(); // Start console detection
      }
    }, 3000); // Start after 3 seconds
  };

  const disableKeyboardShortcuts = (e) => {
    // On mobile, only prevent F12 (which mobile keyboards rarely have)
    if (isMobile()) {
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      return; // Allow all other keys on mobile
    }

    // Desktop keyboard protection
    // Disable F12
    if (e.key === 'F12') {
      e.preventDefault();
      redirectToBlankPage();
      return false;
    }

    // Disable Ctrl+Shift+I (Inspector)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      redirectToBlankPage();
      return false;
    }

    // Disable Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      redirectToBlankPage();
      return false;
    }

    // Disable Ctrl+U (View Source)
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      redirectToBlankPage();
      return false;
    }

    // Disable Ctrl+Shift+C (Element Inspector)
    if (e.ctrlKey && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      redirectToBlankPage();
      return false;
    }

    // Allow text selection and normal operations on desktop
    // These are too aggressive and interfere with normal usage
  };

  const disableRightClick = (e) => {
    e.preventDefault();
    // Don't immediately redirect, just disable right click
    return false;
  };

  const disableTextSelection = () => {
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.mozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    
    // Disable drag
    document.ondragstart = () => false;
    document.onselectstart = () => false;
  };

  const protectConsole = () => {
    // Override console methods
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    
    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.clear = () => {};
    console.dir = () => {};
    console.dirxml = () => {};
    console.table = () => {};
    console.trace = () => {};
    console.group = () => {};
    console.groupCollapsed = () => {};
    console.groupEnd = () => {};
    console.time = () => {};
    console.timeEnd = () => {};
    console.profile = () => {};
    console.profileEnd = () => {};
    console.count = () => {};
  };

  const addWarningMessages = () => {
    // Add warning message to console (before overriding)
    const style = 'color: red; font-size: 20px; font-weight: bold;';
    console.log('%c⚠️ WARNING: Developer tools detected!', style);
    console.log('%cThis website is protected. Unauthorized access is prohibited.', 'color: red; font-size: 14px;');
  };

  useEffect(() => {
    // Check if development mode is enabled
    if (securityConfig.developmentMode) {
      console.log('DevTools protection disabled in development mode');
      return;
    }

    // Mobile-specific handling
    if (isMobile()) {
      console.log('Mobile device detected - using minimal protection');
      // Only add basic keyboard protection on mobile
      document.addEventListener('keydown', disableKeyboardShortcuts, true);
      return () => {
        document.removeEventListener('keydown', disableKeyboardShortcuts, true);
      };
    }

    if (isProtectionActive.current) {
      // IMMEDIATE detection for pre-opened DevTools - with error handling
      try {
        const devToolsAlreadyOpen = immediateDetection();
        
        if (devToolsAlreadyOpen) {
          return; // Stop here if DevTools already detected
        }
      } catch (error) {
        console.log('Immediate detection error (non-critical):', error);
      }

      // Add warning messages first (desktop only)
      try {
        addWarningMessages();
        
        // Start protection (desktop only)
        detectDevTools();
        disableTextSelection();
        protectConsole();
      } catch (error) {
        console.log('Protection setup error:', error);
      }

      // Add event listeners
      document.addEventListener('keydown', disableKeyboardShortcuts, true);
      document.addEventListener('contextmenu', disableRightClick, true);
      
      // Disable print screen
      document.addEventListener('keyup', (e) => {
        if (e.key === 'PrintScreen') {
          navigator.clipboard.writeText('');
        }
      });

      // Remove the aggressive debugging check that was causing false positives

      // Cleanup function
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        document.removeEventListener('keydown', disableKeyboardShortcuts, true);
        document.removeEventListener('contextmenu', disableRightClick, true);
      };
    }
  }, []);

  const disableProtection = () => {
    isProtectionActive.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const enableProtection = () => {
    isProtectionActive.current = true;
  };

  return {
    disableProtection,
    enableProtection,
    isProtectionActive: isProtectionActive.current
  };
}; 